
import { ArrowRight } from "lucide-react";
import { Button, ContactButton } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { M365CaseStudySummary } from "@/components/services/cybersecurity/M365CaseStudySummary";

export const ValuePropositionSection = () => {
  return (
    <section className="py-16 bg-gradient-to-br from-purple-50 to-white" aria-labelledby="value-proposition-heading">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 id="value-proposition-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            How We Contain the Chaos
          </h2>
          <p className="text-xl text-gray-600">
            Your Path to Clarity
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <div className="space-y-8">
            <p className="text-lg text-gray-700">
              We provide the strategic security guidance you need without the overwhelming complexity. Our approach transforms security from a source of anxiety into a business enabler.
            </p>
            
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Focused Executive Guidance</h3>
                <p className="text-gray-600">
                  We translate complex security challenges into clear business decisions, helping you understand exactly where to invest for maximum impact.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Practical Compliance Solutions</h3>
                <p className="text-gray-600">
                  Stop drowning in frameworks and start with what matters. We build compliance programs that protect your business without unnecessary bureaucracy.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Security as a Business Accelerator</h3>
                <p className="text-gray-600">
                  Transform security from a cost center into a competitive advantage that helps you win deals, enter new markets, and grow with confidence.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Clear, Actionable Roadmap</h3>
                <p className="text-gray-600">
                  No more guesswork. Get a prioritized security plan aligned with your business goals that shows exactly what to do next and why it matters.
                </p>
              </div>
            </div>
            
            <div className="pt-6">
              <ContactButton 
                className="bg-edgefi-purple hover:bg-edgefi-purple/90 text-white shadow-md group"
                modalTitle="Start Your Security Transformation" 
                modalDescription="Let us help you transform security from a source of anxiety into a business enabler."
              >
                Start Your Transformation
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </ContactButton>
            </div>
          </div>
          
          <M365CaseStudySummary 
            title="Financial Firm Unlocks $320K in Savings by Maximizing Microsoft 365 Security"
            industry="Financial Services"
            challenge="A financial services firm was struggling with tool sprawl and underutilized Microsoft 365 E5 licensing. They had 14 different security tools with overlapping functionality, while only using 15% of their Microsoft 365 E5 security features."
            solution="We implemented a three-phase strategy to fully leverage their Microsoft 365 E5 investment while consolidating their security toolset."
            results={[
              "40% improvement in NIST CSF score",
              "$320K annual security tool savings",
              "85% reduction in security alerts",
              "67% faster incident response"
            ]}
            cta="Read the Full Case Study"
            link="/case-studies/optimized-m365-security-transformation"
          />
        </div>
      </div>
    </section>
  );
};
